import { Button, Divider, Form, Row, Spin } from "antd";
import {
  downloadDashboardDailyReport,
  fetchDashboardDailyReport,
  ResponseInventoryControlRegistry,
} from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import {
  handleAxiosError,
  handleCsvDownload,
} from "common/helpers/generalUtils";
import { useDateRange } from "context/DateRangeContext";
import { useClient } from "hooks/useClient";
import useLoggedUser from "hooks/useLoggedUser";
import { useCallback, useEffect, useState } from "react";
import {
  EXPORT_REPORT_PERMISSION,
  getPagePermissions,
} from "security/permissions";
import { DashboardTable } from "./DashboardTable";
import Search from "antd/es/input/Search";

function DashboardScreen() {
  const { permissions } = useLoggedUser();
  const exportReportsPermissions = getPagePermissions(
    EXPORT_REPORT_PERMISSION,
    permissions,
  );
  const [isLoading, setIsLoading] = useState(false);
  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate } = getFormattedDates();
  const { client } = useClient();
  const [filterText, setFilterText] = useState<string>("");
  const [summary, setSummary] = useState<ResponseInventoryControlRegistry[]>(
    [],
  );

  const reloadTable = useCallback(() => {
    setIsLoading(true);
    fetchDashboardDailyReport({
      client,
      query: { date: startDate },
    })
      .then(({ data }) => setSummary(data ?? []))
      .catch((err) =>
        handleAxiosError(
          err,
          "No fue posible obtener los registros operativos",
        ),
      )
      .finally(() => setIsLoading(false));
  }, [client, startDate]);

  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  function downloadReporte() {
    setIsLoading(true);
    downloadDashboardDailyReport({
      client,
      query: {
        date: startDate,
      },
    })
      .then(( data ) =>
        handleCsvDownload(data),
      )
      .catch((err) =>
        handleAxiosError(err, "No fue posible descargar el reporte"),
      )
      .finally(() => setIsLoading(false));
  }

  return (
    <Spin spinning={isLoading} size="large" tip="Cargando...">
      <div>
        <Form layout="vertical">
          <Row gutter={16} align="bottom">
            <ResponsiveCol>
              <FormItem label="Seleccionar el día a consultar">
                <DateRangePicker
                  config={{
                    allowedTypes: [DateFilterType.DAY],
                    defaultType: DateFilterType.DAY,
                    showQuickFilters: true,
                  }}
                  rangeTimeValue={rangeTimeValue}
                  setRangeTimeValue={setRangeTimeValue}
                />
              </FormItem>
            </ResponsiveCol>

            <ResponsiveCol>
              <FormItem label="Buscar por número de permiso o nombre">
                <Search
                  onChange={(e) => setFilterText(e.target.value)}
                  value={filterText}
                />
              </FormItem>
            </ResponsiveCol>

            <ResponsiveCol>
              <FormItem>
                <Button
                  style={{ width: "100%" }}
                  onClick={downloadReporte}
                  disabled={!exportReportsPermissions || summary.length === 0}
                >
                  Descargar CSV
                </Button>
              </FormItem>
            </ResponsiveCol>
          </Row>

          <Row gutter={16}>
            <div style={{ width: "100%", overflowX: "auto" }}>
              <DashboardTable data={summary} filterText={filterText} />
            </div>
          </Row>

          <Divider />
        </Form>
      </div>
    </Spin>
  );
}

export default DashboardScreen;
